
// Utility functions for exporting purchase data to PDF and Excel formats
export const exportComprasToPDF = (compras: any[], fornecedores: any[]) => {
  try {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Por favor, permita pop-ups para exportar o PDF')
      return
    }

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'cotacao': return 'Em Cotação'
        case 'aprovado': return 'Aprovado'
        case 'pedido_enviado': return 'Pedido Enviado'
        case 'entregue': return 'Entregue'
        case 'cancelado': return 'Cancelado'
        default: return status
      }
    }

    const getFornecedorNome = (fornecedorId: string) => {
      const fornecedor = fornecedores.find(f => f._id === fornecedorId)
      return fornecedor ? fornecedor.nome : 'N/A'
    }

    const totalGeral = compras.reduce((total, compra) => total + compra.valorTotal, 0)

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Compras</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .summary { margin-bottom: 30px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; }
          .summary-item { display: inline-block; margin-right: 30px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .status-cotacao { background-color: #fef3c7; color: #92400e; }
          .status-aprovado { background-color: #d1fae5; color: #065f46; }
          .status-pedido_enviado { background-color: #dbeafe; color: #1e40af; }
          .status-entregue { background-color: #dcfce7; color: #166534; }
          .status-cancelado { background-color: #fee2e2; color: #991b1b; }
          .total-section { margin-top: 20px; text-align: right; font-size: 16px; font-weight: bold; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RELATÓRIO DE COMPRAS</h1>
          <p>Gerado em: ${formatDate(new Date().toISOString())}</p>
        </div>
        
        <div class="summary">
          <h3>Resumo Geral</h3>
          <div class="summary-item">
            <strong>Total de Compras:</strong> ${compras.length}
          </div>
          <div class="summary-item">
            <strong>Em Cotação:</strong> ${compras.filter(c => c.status === 'cotacao').length}
          </div>
          <div class="summary-item">
            <strong>Aprovadas:</strong> ${compras.filter(c => c.status === 'aprovado').length}
          </div>
          <div class="summary-item">
            <strong>Entregues:</strong> ${compras.filter(c => c.status === 'entregue').length}
          </div>
          <div class="summary-item">
            <strong>Valor Total:</strong> ${formatCurrency(totalGeral)}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Número</th>
              <th>Insumo</th>
              <th>Quantidade</th>
              <th>Fornecedor</th>
              <th>Valor Total</th>
              <th>Status</th>
              <th>Data Compra</th>
              <th>Entrega Prevista</th>
            </tr>
          </thead>
          <tbody>
            ${compras.map(compra => `
              <tr>
                <td>${compra.numero}</td>
                <td>${compra.nomeInsumo}</td>
                <td>${compra.quantidade} ${compra.unidadeMedida}</td>
                <td>${getFornecedorNome(compra.fornecedorSelecionado || '')}</td>
                <td>${formatCurrency(compra.valorTotal)}</td>
                <td class="status-${compra.status}">${getStatusLabel(compra.status)}</td>
                <td>${formatDate(compra.dataCompra)}</td>
                <td>${formatDate(compra.dataEntregaPrevista)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <p>VALOR TOTAL GERAL: ${formatCurrency(totalGeral)}</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  } catch (error) {
    console.error('Erro ao exportar PDF:', error)
    alert('Erro ao exportar PDF. Tente novamente.')
  }
}

export const exportComprasToExcel = (compras: any[], fornecedores: any[]) => {
  try {
    const formatCurrency = (value: number) => {
      return value.toFixed(2).replace('.', ',')
    }

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'cotacao': return 'Em Cotação'
        case 'aprovado': return 'Aprovado'
        case 'pedido_enviado': return 'Pedido Enviado'
        case 'entregue': return 'Entregue'
        case 'cancelado': return 'Cancelado'
        default: return status
      }
    }

    const getFornecedorNome = (fornecedorId: string) => {
      const fornecedor = fornecedores.find(f => f._id === fornecedorId)
      return fornecedor ? fornecedor.nome : 'N/A'
    }

    let csvContent = ''
    
    // Header
    csvContent += 'RELATÓRIO DE COMPRAS\n'
    csvContent += `Gerado em: ${formatDate(new Date().toISOString())}\n\n`
    
    // Summary
    csvContent += 'RESUMO GERAL\n'
    csvContent += `Total de Compras,${compras.length}\n`
    csvContent += `Em Cotação,${compras.filter(c => c.status === 'cotacao').length}\n`
    csvContent += `Aprovadas,${compras.filter(c => c.status === 'aprovado').length}\n`
    csvContent += `Entregues,${compras.filter(c => c.status === 'entregue').length}\n`
    csvContent += `Valor Total,${formatCurrency(compras.reduce((total, c) => total + c.valorTotal, 0))}\n\n`

    // Table header
    csvContent += 'Número,Insumo,Quantidade,Unidade,Fornecedor,Valor Total,Status,Data Compra,Entrega Prevista,Observações\n'

    // Table data
    compras.forEach(compra => {
      csvContent += `"${compra.numero}","${compra.nomeInsumo}",${compra.quantidade},"${compra.unidadeMedida}","${getFornecedorNome(compra.fornecedorSelecionado || '')}",${formatCurrency(compra.valorTotal)},"${getStatusLabel(compra.status)}","${formatDate(compra.dataCompra)}","${formatDate(compra.dataEntregaPrevista)}","${compra.observacoes || ''}"\n`
    })

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `relatorio_compras_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      alert('Seu navegador não suporta download automático. Por favor, copie o conteúdo e salve manualmente.')
    }
  } catch (error) {
    console.error('Erro ao exportar Excel:', error)
    alert('Erro ao exportar Excel. Tente novamente.')
  }
}

export const exportFornecedoresToPDF = (fornecedores: any[]) => {
  try {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Por favor, permita pop-ups para exportar o PDF')
      return
    }

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }

    const renderStars = (rating: number) => {
      return '★'.repeat(rating) + '☆'.repeat(5 - rating)
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Fornecedores</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .status-ativo { background-color: #d1fae5; color: #065f46; }
          .status-inativo { background-color: #fee2e2; color: #991b1b; }
          .status-bloqueado { background-color: #fef3c7; color: #92400e; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RELATÓRIO DE FORNECEDORES</h1>
          <p>Gerado em: ${new Date().toLocaleDateString('pt-BR')}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Segmento</th>
              <th>Contato</th>
              <th>Confiabilidade</th>
              <th>Tempo Entrega</th>
              <th>Preço Médio</th>
              <th>Status</th>
              <th>Observações</th>
            </tr>
          </thead>
          <tbody>
            ${fornecedores.map(fornecedor => `
              <tr>
                <td>${fornecedor.nome}</td>
                <td>${fornecedor.segmento}</td>
                <td>${fornecedor.contato}</td>
                <td>${renderStars(fornecedor.confiabilidade)} (${fornecedor.confiabilidade}/5)</td>
                <td>${fornecedor.tempoEntregaMedio} dias</td>
                <td>${formatCurrency(fornecedor.precoMedio)}</td>
                <td class="status-${fornecedor.bloqueado ? 'bloqueado' : (fornecedor.ativo ? 'ativo' : 'inativo')}">
                  ${fornecedor.bloqueado ? 'Bloqueado' : (fornecedor.ativo ? 'Ativo' : 'Inativo')}
                </td>
                <td>${fornecedor.observacoes || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  } catch (error) {
    console.error('Erro ao exportar PDF:', error)
    alert('Erro ao exportar PDF. Tente novamente.')
  }
}

// NEW FUNCTIONS FOR PURCHASE ORDERS EXPORT

export const exportOrdensCompraToPDF = (ordensCompra: any[], fornecedores: any[]) => {
  try {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Por favor, permita pop-ups para exportar o PDF')
      return
    }

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'emitida': return 'Emitida'
        case 'enviada': return 'Enviada'
        case 'confirmada': return 'Confirmada'
        case 'em_producao': return 'Em Produção'
        case 'enviado': return 'Enviado'
        case 'entregue': return 'Entregue'
        case 'cancelada': return 'Cancelada'
        default: return status
      }
    }

    const getFornecedorInfo = (fornecedorId: string) => {
      const fornecedor = fornecedores.find(f => f._id === fornecedorId)
      return fornecedor ? {
        nome: fornecedor.nome,
        contato: fornecedor.contato,
        segmento: fornecedor.segmento
      } : {
        nome: 'N/A',
        contato: 'N/A',
        segmento: 'N/A'
      }
    }

    const totalGeral = ordensCompra.reduce((total, ordem) => total + ordem.valorTotal, 0)

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Ordens de Compra</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .company-info { margin-bottom: 20px; }
          .summary { margin-bottom: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px; border: 1px solid #dee2e6; }
          .summary-item { display: inline-block; margin-right: 30px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 11px; }
          th { background-color: #f8f9fa; font-weight: bold; text-transform: uppercase; }
          .status-emitida { background-color: #fff3cd; color: #856404; }
          .status-enviada { background-color: #d1ecf1; color: #0c5460; }
          .status-confirmada { background-color: #d4edda; color: #155724; }
          .status-em_producao { background-color: #cce5ff; color: #004085; }
          .status-entregue { background-color: #d1e7dd; color: #0f5132; }
          .status-cancelada { background-color: #f8d7da; color: #721c24; }
          .total-section { margin-top: 20px; text-align: right; font-size: 16px; font-weight: bold; background-color: #e9ecef; padding: 15px; border-radius: 5px; }
          .ordem-detail { margin-bottom: 15px; padding: 10px; border-left: 3px solid #007bff; background-color: #f8f9fa; }
          @media print { body { margin: 0; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <h1>RELATÓRIO DE ORDENS DE COMPRA</h1>
            <h2>GestPRO - Sistema de Gestão</h2>
          </div>
          <p><strong>Data de Geração:</strong> ${formatDate(new Date().toISOString())} às ${new Date().toLocaleTimeString('pt-BR')}</p>
          <p><strong>Total de Ordens:</strong> ${ordensCompra.length}</p>
        </div>
        
        <div class="summary">
          <h3>📊 Resumo Executivo</h3>
          <div class="summary-item">
            <strong>Total de Ordens:</strong> ${ordensCompra.length}
          </div>
          <div class="summary-item">
            <strong>Emitidas:</strong> ${ordensCompra.filter(o => o.status === 'emitida').length}
          </div>
          <div class="summary-item">
            <strong>Enviadas:</strong> ${ordensCompra.filter(o => o.status === 'enviada').length}
          </div>
          <div class="summary-item">
            <strong>Confirmadas:</strong> ${ordensCompra.filter(o => o.status === 'confirmada').length}
          </div>
          <div class="summary-item">
            <strong>Entregues:</strong> ${ordensCompra.filter(o => o.status === 'entregue').length}
          </div>
          <div class="summary-item">
            <strong>Valor Total:</strong> ${formatCurrency(totalGeral)}
          </div>
        </div>

        <h3>📋 Detalhamento das Ordens de Compra</h3>
        <table>
          <thead>
            <tr>
              <th>Nº Ordem</th>
              <th>Fornecedor</th>
              <th>Insumo/Material</th>
              <th>Quantidade</th>
              <th>Valor Unitário</th>
              <th>Valor Total</th>
              <th>Status</th>
              <th>Data Emissão</th>
              <th>Prazo Entrega</th>
              <th>Obra</th>
            </tr>
          </thead>
          <tbody>
            ${ordensCompra.map(ordem => {
              const fornecedorInfo = getFornecedorInfo(ordem.fornecedorId || '')
              return `
                <tr>
                  <td><strong>${ordem.numero}</strong></td>
                  <td>
                    <div><strong>${fornecedorInfo.nome}</strong></div>
                    <div style="font-size: 10px; color: #666;">${fornecedorInfo.contato}</div>
                  </td>
                  <td>
                    <div><strong>${ordem.nomeInsumo}</strong></div>
                    <div style="font-size: 10px; color: #666;">${ordem.descricaoInsumo || ''}</div>
                  </td>
                  <td>${ordem.quantidade} ${ordem.unidadeMedida || ''}</td>
                  <td>${formatCurrency(ordem.precoUnitario || 0)}</td>
                  <td><strong>${formatCurrency(ordem.valorTotal)}</strong></td>
                  <td>
                    <span class="status-${ordem.status}" style="padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: bold;">
                      ${getStatusLabel(ordem.status)}
                    </span>
                  </td>
                  <td>${formatDate(ordem.dataEmissao)}</td>
                  <td>${formatDate(ordem.prazoEntrega || ordem.dataEmissao)}</td>
                  <td style="font-size: 10px;">${ordem.nomeObra || 'N/A'}</td>
                </tr>
              `
            }).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <p>💰 VALOR TOTAL GERAL: ${formatCurrency(totalGeral)}</p>
          <p style="font-size: 12px; margin-top: 10px;">
            Média por ordem: ${formatCurrency(totalGeral / ordensCompra.length)}
          </p>
        </div>

        <div style="margin-top: 40px; padding: 20px; border: 1px solid #ddd; background-color: #f8f9fa;">
          <h4>📝 Observações Importantes:</h4>
          <ul style="font-size: 12px;">
            <li>Este relatório contém ${ordensCompra.length} ordens de compra</li>
            <li>Valores apresentados em Real Brasileiro (R$)</li>
            <li>Status atualizado até a data de geração deste relatório</li>
            <li>Para dúvidas, entre em contato com o setor de compras</li>
          </ul>
        </div>

        <div style="margin-top: 30px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 10px;">
          <p>GestPRO - Sistema de Gestão | Relatório gerado automaticamente</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  } catch (error) {
    console.error('Erro ao exportar PDF de Ordens de Compra:', error)
    alert('Erro ao exportar PDF. Tente novamente.')
  }
}

export const exportOrdensCompraToExcel = (ordensCompra: any[], fornecedores: any[]) => {
  try {
    const formatCurrency = (value: number) => {
      return value.toFixed(2).replace('.', ',')
    }

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'emitida': return 'Emitida'
        case 'enviada': return 'Enviada'
        case 'confirmada': return 'Confirmada'
        case 'em_producao': return 'Em Produção'
        case 'enviado': return 'Enviado'
        case 'entregue': return 'Entregue'
        case 'cancelada': return 'Cancelada'
        default: return status
      }
    }

    const getFornecedorNome = (fornecedorId: string) => {
      const fornecedor = fornecedores.find(f => f._id === fornecedorId)
      return fornecedor ? fornecedor.nome : 'N/A'
    }

    const getFornecedorContato = (fornecedorId: string) => {
      const fornecedor = fornecedores.find(f => f._id === fornecedorId)
      return fornecedor ? fornecedor.contato : 'N/A'
    }

    let csvContent = ''
    
    // Header
    csvContent += 'RELATÓRIO DE ORDENS DE COMPRA - GestPRO\n'
    csvContent += `Gerado em: ${formatDate(new Date().toISOString())} às ${new Date().toLocaleTimeString('pt-BR')}\n\n`
    
    // Summary
    csvContent += 'RESUMO EXECUTIVO\n'
    csvContent += `Total de Ordens,${ordensCompra.length}\n`
    csvContent += `Emitidas,${ordensCompra.filter(o => o.status === 'emitida').length}\n`
    csvContent += `Enviadas,${ordensCompra.filter(o => o.status === 'enviada').length}\n`
    csvContent += `Confirmadas,${ordensCompra.filter(o => o.status === 'confirmada').length}\n`
    csvContent += `Entregues,${ordensCompra.filter(o => o.status === 'entregue').length}\n`
    csvContent += `Canceladas,${ordensCompra.filter(o => o.status === 'cancelada').length}\n`
    csvContent += `Valor Total,${formatCurrency(ordensCompra.reduce((total, o) => total + o.valorTotal, 0))}\n`
    csvContent += `Valor Médio por Ordem,${formatCurrency(ordensCompra.reduce((total, o) => total + o.valorTotal, 0) / ordensCompra.length)}\n\n`

    // Table header
    csvContent += 'DETALHAMENTO DAS ORDENS\n'
    csvContent += 'Número Ordem,Fornecedor,Contato Fornecedor,Insumo/Material,Descrição,Quantidade,Unidade,Valor Unitário,Valor Total,Status,Data Emissão,Prazo Entrega,Obra,Observações\n'

    // Table data
    ordensCompra.forEach(ordem => {
      const fornecedorNome = getFornecedorNome(ordem.fornecedorId || '')
      const fornecedorContato = getFornecedorContato(ordem.fornecedorId || '')
      
      csvContent += `"${ordem.numero}","${fornecedorNome}","${fornecedorContato}","${ordem.nomeInsumo}","${ordem.descricaoInsumo || ''}",${ordem.quantidade},"${ordem.unidadeMedida || ''}",${formatCurrency(ordem.precoUnitario || 0)},${formatCurrency(ordem.valorTotal)},"${getStatusLabel(ordem.status)}","${formatDate(ordem.dataEmissao)}","${formatDate(ordem.prazoEntrega || ordem.dataEmissao)}","${ordem.nomeObra || 'N/A'}","${ordem.observacoes || ''}"\n`
    })

    // Additional statistics
    csvContent += '\n\nESTATÍSTICAS ADICIONAIS\n'
    csvContent += `Maior Valor,${formatCurrency(Math.max(...ordensCompra.map(o => o.valorTotal)))}\n`
    csvContent += `Menor Valor,${formatCurrency(Math.min(...ordensCompra.map(o => o.valorTotal)))}\n`
    
    // Status breakdown
    csvContent += '\nDISTRIBUIÇÃO POR STATUS\n'
    const statusCounts = ordensCompra.reduce((acc, ordem) => {
      acc[ordem.status] = (acc[ordem.status] || 0) + 1
      return acc
    }, {} as any)
    
    Object.entries(statusCounts).forEach(([status, count]) => {
      csvContent += `${getStatusLabel(status)},${count}\n`
    })

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `relatorio_ordens_compra_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      alert('Seu navegador não suporta download automático. Por favor, copie o conteúdo e salve manualmente.')
    }
  } catch (error) {
    console.error('Erro ao exportar Excel de Ordens de Compra:', error)
    alert('Erro ao exportar Excel. Tente novamente.')
  }
}
